/**
 * Hook for tracking time spent on wizard steps
 */

import { useEffect, useRef, useCallback } from "react";
import { useAnalytics } from "@/analytics/AnalyticsContext";
import { WIZARD_EVENTS } from "@/analytics/events";
import { WIZARD_STEP_NAMES } from "@/components/Wizard/wizardStepNames";

export function useWizardStepTimer(
  currentStep: number,
  userId?: string,
  isModal: boolean = false
) {
  const { trackEvent } = useAnalytics();
  const stepStartTime = useRef<Date | null>(null);

  const getStepName = (step: number, isModal: boolean): string => {
    const stepNames = isModal ? WIZARD_STEP_NAMES.MODAL : WIZARD_STEP_NAMES.NON_MODAL;
    return stepNames[step as keyof typeof stepNames] || `step_${step}`;
  };

  useEffect(() => {
    stepStartTime.current = new Date();
  }, [currentStep]);

  const trackStepCompletion = useCallback(() => {
    if (stepStartTime.current) {
      const now = new Date();
      const timeSpentMs = now.getTime() - stepStartTime.current.getTime();
      const timeSpentSeconds = Math.round(timeSpentMs / 1000);

      if (timeSpentSeconds > 1) {
        trackEvent(WIZARD_EVENTS.WIZARD_STEP_COMPLETED, {
          user_id: userId,
          step_number: currentStep,
          step_name: getStepName(currentStep, isModal),
          time_spent_seconds: timeSpentSeconds,
          is_modal: isModal,
        });
      }
    }
  }, [currentStep, userId, isModal, trackEvent]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      trackStepCompletion();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [trackStepCompletion]);

  return {
    trackStepCompletion,
  };
}
